/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle2, XCircle, Circle, Clock, AlertTriangle, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_STYLES = {
  Induction: 'bg-blue-50 text-blue-700 border-blue-200',
  Training: 'bg-amber-50 text-amber-700 border-amber-200',
  Performance: 'bg-rose-50 text-rose-700 border-rose-200',
  Stabilization: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Graduation: 'bg-violet-50 text-violet-700 border-violet-200',
};

function getESTTime() {
  const now = new Date();
  const est = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  return est;
}

function isPastDeadline() {
  const est = getESTTime();
  return est.getHours() > 19 || (est.getHours() === 19 && est.getMinutes() >= 30);
}

function NotificationBanner({ pending }) {
  const past = isPastDeadline();
  if (!pending || pending.length === 0) return null;

  const trainerPending = pending.filter(p => p.pending_type === 'trainer_action');

  return (
    <div data-testid="notification-banner" className={`border p-4 mb-6 ${past ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className={past ? 'text-red-600 mt-0.5' : 'text-amber-600 mt-0.5'} />
        <div className="flex-1">
          <h3 className={`font-heading text-sm font-bold ${past ? 'text-red-800' : 'text-amber-800'}`}>
            {past ? 'URGENT: Day-End Deadline Passed (7:30 PM EST)' : 'Pending Actions Required'}
          </h3>
          {trainerPending.length > 0 && (
            <div className="mt-2">
              <p className="font-body text-xs font-semibold text-[#4B5563] uppercase tracking-wider mb-1">Trainer Actions Needed:</p>
              {trainerPending.map((p, i) => (
                <p key={i} className="font-body text-sm text-[#0A0A0A]">
                  {p.trainee_name} - Day {p.pending_days.join(', ')} awaiting review
                </p>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepCard({ day, structure, progress, canTrainerAct, canManagerAct, canVpAct, isAgent, onTrainerAction, onManagerAction, onVpAction }) {
  const [trainerNote, setTrainerNote] = useState(progress?.trainer_note || '');
  const [managerNote, setManagerNote] = useState(progress?.manager_note || '');
  const [vpNote, setVpNote] = useState(progress?.vp_note || '');
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const trainerCharCount = trainerNote.trim().length;
  const managerCharCount = managerNote.trim().length;
  const trainerNoteValid = trainerCharCount >= 1;
  const managerNoteValid = true; // Manager note is NOT mandatory

  const status = progress?.status || 'pending';
  const trainerAction = progress?.trainer_action;
  const managerAction = progress?.manager_action;
  const vpAction = progress?.vp_action;

  const isPending = status === 'pending';
  const isActive = status === 'in_progress';
  const isApproved = trainerAction === 'approved';
  const isRejected = trainerAction === 'rejected';
  const showVpGate = day.day === 15 && isApproved;

  // Determine visual state
  let borderColor = '#E5E7EB';
  let bgColor = 'bg-white';
  let opacity = '';
  if (isApproved) { borderColor = '#16A34A'; }
  else if (isRejected) { borderColor = '#DC2626'; }
  else if (isActive) { borderColor = '#0047FF'; }
  else if (isPending) { opacity = 'opacity-40'; }

  // Auto-expand active/rejected steps
  const isActionable = (isActive || isRejected) && canTrainerAct;
  const shouldShowActions = isActionable || expanded;

  const handleTrainerAction = async (action) => {
    setSubmitting(true);
    try {
      await onTrainerAction(day.day, action, trainerNote);
    } finally {
      setSubmitting(false);
    }
  };

  const handleManagerAction = async (action) => {
    setSubmitting(true);
    try {
      await onManagerAction(day.day, action, managerNote);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVpAction = async (action) => {
    setSubmitting(true);
    try {
      await onVpAction(day.day, action, vpNote);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={`journey-step-${day.day}`} className={`${opacity}`}>
      <div className="border p-5 transition-all" style={{ borderColor }}>
        {/* Header */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex-shrink-0">
            {isApproved ? <CheckCircle2 size={24} className="text-[#16A34A]" strokeWidth={2.5} /> :
             isRejected ? <XCircle size={24} className="text-[#DC2626]" strokeWidth={2.5} /> :
             isActive ? <Clock size={24} className="text-[#0047FF]" strokeWidth={2} /> :
             <Circle size={24} className="text-[#E5E7EB]" strokeWidth={1.5} />}
          </div>
          <span className="font-heading text-2xl font-black text-[#0A0A0A]">{String(day.day).padStart(2, '0')}</span>
          <span className={`inline-block px-2 py-0.5 text-xs font-body font-semibold border ${PHASE_STYLES[day.phase] || ''}`}>{day.phase}</span>
          <h3 className="font-heading text-base font-semibold text-[#0A0A0A] flex-1">{day.title}</h3>

          {trainerAction && (
            <Badge className={`rounded-none font-body text-xs ${isApproved ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'}`}>
              {trainerAction === 'approved' ? 'Approved' : 'Rejected'}
            </Badge>
          )}

          {!isPending && (
            expanded ? <ChevronUp size={18} className="text-[#4B5563]" /> : <ChevronDown size={18} className="text-[#4B5563]" />
          )}
        </div>

        {/* Expanded content */}
        {(shouldShowActions || expanded || isActive) && (
          <div className="mt-4 pl-9 space-y-3">
            {/* Activities */}
            <ul className="space-y-1.5">
              {day.activities.map((activity, i) => (
                <li key={i} className="font-body text-sm text-[#4B5563] flex items-start gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#4B5563] mt-2 flex-shrink-0" />
                  {activity}
                </li>
              ))}
            </ul>

            {day.target && (
              <div className="flex items-center gap-2 pt-2 border-t border-[#E5E7EB]">
                <span className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Target:</span>
                <span className="font-body text-sm font-medium text-[#0A0A0A]">{day.target}</span>
              </div>
            )}

            {/* Show existing trainer note if already acted */}
            {trainerAction && progress?.trainer_note && (
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 mt-2">
                <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563] mb-1">Trainer Note</p>
                <p className="font-body text-sm text-[#0A0A0A]">{progress.trainer_note}</p>
              </div>
            )}

            {/* Next day countdown */}
            {isApproved && day.day < 16 && (() => {
              const approvalTime = progress?.trainer_action_at;
              if (!approvalTime) return null;
              const approvalDate = new Date(approvalTime);
              const tomorrow = new Date(approvalDate);
              tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
              tomorrow.setUTCHours(0, 0, 0, 0);
              const now = new Date();
              const diff = tomorrow - now;
              if (diff <= 0) return null;
              const hours = Math.floor(diff / (1000 * 60 * 60));
              const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
              // Skip countdown for VP-gated step
              if (day.day === 15 && !progress?.vp_action) return null;
              return (
                <div className="bg-blue-50 border border-blue-200 p-3 mt-2 flex items-center gap-2" data-testid={`countdown-${day.day}`}>
                  <Clock size={16} className="text-[#0047FF]" />
                  <span className="font-body text-sm text-[#0047FF] font-medium">
                    Next step unlocks in {hours}h {mins}m
                  </span>
                </div>
              );
            })()}

            {/* Trainer action area - only for active/rejected steps */}
            {isActionable && (
              <div className="border-t border-[#E5E7EB] pt-4 mt-3 space-y-3">
                <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">
                  {isRejected ? 'Re-evaluate this step' : 'Trainer Review'}
                </p>
                <Textarea
                  data-testid={`trainer-note-${day.day}`}
                  value={trainerNote}
                  onChange={e => setTrainerNote(e.target.value)}
                  className="rounded-none font-body"
                  rows={5}
                  placeholder="Write your review note for this step..."
                />
                <div className="flex items-center justify-between">
                  <span className="font-body text-xs text-[#4B5563]">
                    {trainerCharCount} characters
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid={`approve-step-${day.day}`}
                    onClick={() => handleTrainerAction('approved')}
                    disabled={submitting || !trainerNoteValid}
                    className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-body text-sm gap-1"
                  >
                    <CheckCircle2 size={16} /> Approve
                  </Button>
                  <Button
                    data-testid={`reject-step-${day.day}`}
                    onClick={() => handleTrainerAction('rejected')}
                    disabled={submitting || !trainerNoteValid}
                    variant="outline"
                    className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-red-50 font-body text-sm gap-1"
                  >
                    <XCircle size={16} /> Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Agent view of notes */}
            {isAgent && progress?.trainer_note && !trainerAction && (
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3">
                <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563] mb-1">Trainer Note</p>
                <p className="font-body text-sm text-[#0A0A0A]">{progress.trainer_note}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ VP CHECKPOINT between Step 15 and 16 ═══ */}
      {showVpGate && (
        <div data-testid="vp-gate" className="my-4 border-2 border-dashed border-[#8B5CF6] bg-violet-50/50 p-5">
          <div className="flex items-center gap-3 mb-4">
            <Shield size={22} className="text-[#8B5CF6]" />
            <h3 className="font-heading text-base font-bold text-[#0A0A0A]">VP - Operations Checkpoint</h3>
            <span className="font-body text-xs text-[#4B5563]">Mandatory approval before Graduation</span>
            {vpAction && (
              <Badge className={`rounded-none font-body text-xs ml-auto ${vpAction === 'approved' ? 'bg-[#16A34A] text-white' : 'bg-[#DC2626] text-white'}`}>
                VP {vpAction === 'approved' ? 'Approved' : 'Rejected'}
              </Badge>
            )}
          </div>

          {progress?.trainer_note && (
            <div className="bg-white border border-[#E5E7EB] p-3 mb-3">
              <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563] mb-1">Trainer Note (Step 15)</p>
              <p className="font-body text-sm text-[#0A0A0A]">{progress.trainer_note}</p>
            </div>
          )}

          {vpAction && progress?.vp_note && (
            <div className="bg-white border border-[#E5E7EB] p-3 mb-3">
              <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563] mb-1">VP Note</p>
              <p className="font-body text-sm text-[#0A0A0A]">{progress.vp_note}</p>
            </div>
          )}

          {canVpAct && !vpAction && (
            <div className="space-y-3 mt-3">
              <Textarea
                data-testid="vp-gate-note"
                value={vpNote}
                onChange={e => setVpNote(e.target.value)}
                className="rounded-none font-body bg-white"
                rows={5}
                placeholder="VP assessment note (optional)..."
              />
              <div className="flex gap-2">
                <Button data-testid="vp-gate-approve" onClick={() => handleVpAction('approved')} disabled={submitting}
                  className="rounded-none bg-[#16A34A] hover:bg-[#15803D] text-white font-body text-sm gap-1">
                  <CheckCircle2 size={16} /> Approve for Graduation
                </Button>
                <Button data-testid="vp-gate-reject" onClick={() => handleVpAction('rejected')} disabled={submitting}
                  variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-red-50 font-body text-sm gap-1">
                  <XCircle size={16} /> Reject
                </Button>
              </div>
            </div>
          )}

          {isAgent && !vpAction && (
            <p className="font-body text-sm text-violet-700 italic">Awaiting VP - Operations approval for Graduation...</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function JourneyPage() {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [structure, setStructure] = useState([]);
  const [progress, setProgress] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const isAgent = user?.role === 'agent';
  const isTrainer = user?.role === 'trainer';
  const isManager = user?.role === 'manager';
  const isVp = user?.role === 'vp_operations';
  const isAdmin = ['master_admin', 'admin'].includes(user?.role);
  const canTrainerAct = isTrainer || isAdmin;
  const canManagerAct = isManager || isAdmin;
  const canVpAct = isVp || isAdmin;

  // Load structure and agents list
  useEffect(() => {
    const load = async () => {
      try {
        const { data: struct } = await axios.get(`${API}/journey/structure`);
        setStructure(struct);

        if (isAgent) {
          setSelectedAgent(user.id);
        } else {
          // Load agents based on role
          const params = isTrainer ? '' : '?role=agent';
          const { data: userList } = await axios.get(`${API}/users${params}`);
          const agentList = isTrainer ? userList : userList.filter(u => u.role === 'agent');
          setAgents(agentList);
          if (agentList.length > 0) setSelectedAgent(agentList[0].id);
        }

        // Load pending actions
        try {
          const { data: pend } = await axios.get(`${API}/journey/pending-actions`);
          setPending(pend);
        } catch (e) { console.debug('pending-actions skipped:', e?.response?.status); }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, isAgent, isTrainer]);

  // Load progress for selected agent
  const loadProgress = useCallback(async () => {
    if (!selectedAgent) { setProgress([]); return; }
    try {
      const { data } = await axios.get(`${API}/journey/progress/${selectedAgent}`);
      setProgress(data);
    } catch (err) {
      console.error(err);
      setProgress([]);
    }
  }, [selectedAgent]);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  // Refresh pending notifications periodically (every 60s)
  const fetchPending = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/journey/pending-actions`);
      setPending(data);
    } catch (e) { console.debug('pending-actions poll skipped:', e?.response?.status); }
  }, []);

  useEffect(() => {
    if (isAgent) return;
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [isAgent, fetchPending]);

  const refreshAgents = async () => {
    if (isAgent) return;
    try {
      const params = isTrainer ? '' : '?role=agent';
      const { data: userList } = await axios.get(`${API}/users${params}`);
      const agentList = isTrainer ? userList : userList.filter(u => u.role === 'agent');
      setAgents(agentList);
    } catch (e) { console.debug('refreshAgents skipped:', e?.response?.status); }
  };

  const handleTrainerAction = async (day, action, note) => {
    try {
      await axios.put(`${API}/journey/progress/${selectedAgent}/${day}/trainer-action`, { action, note });
      toast.success(`Day ${day} ${action === 'approved' ? 'approved' : 'rejected'}`);
      loadProgress();
      refreshAgents();
      try {
        const { data } = await axios.get(`${API}/journey/pending-actions`);
        setPending(data);
      } catch (e) { console.debug('pending-actions refresh skipped:', e?.response?.status); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Action failed');
    }
  };

  const handleManagerAction = async (day, action, note) => {
    try {
      await axios.put(`${API}/journey/progress/${selectedAgent}/${day}/manager-action`, { action, note });
      toast.success(`Manager ${action === 'approved' ? 'approved' : 'rejected'} the gate`);
      loadProgress();
      refreshAgents();
      try {
        const { data } = await axios.get(`${API}/journey/pending-actions`);
        setPending(data);
      } catch (e) { console.debug('pending-actions refresh skipped:', e?.response?.status); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Action failed');
    }
  };

  const handleVpAction = async (day, action, note) => {
    try {
      await axios.put(`${API}/journey/progress/${selectedAgent}/${day}/vp-action`, { action, note });
      toast.success(`VP ${action === 'approved' ? 'approved' : 'rejected'} the checkpoint`);
      loadProgress();
      refreshAgents();
      try {
        const { data } = await axios.get(`${API}/journey/pending-actions`);
        setPending(data);
      } catch (e) { console.debug('pending-actions refresh skipped:', e?.response?.status); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Action failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#0047FF] border-t-transparent animate-spin" /></div>;
  }

  const getProgressForDay = (day) => progress.find(p => p.day === day);
  const selectedAgentData = agents.find(a => a.id === selectedAgent);
  const isTerminated = isAgent ? (user?.journey_status === 'terminated') : (selectedAgentData?.journey_status === 'terminated');
  const terminationInfo = isAgent ? user : selectedAgentData;

  return (
    <div data-testid="journey-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#0A0A0A] tracking-tight">Training Journey</h1>
        <p className="font-body text-sm text-[#4B5563] mt-1">
          {isAgent ? 'Your 16-day training journey' : 'Review and manage trainee progress'}
        </p>
      </div>

      {/* Notification Banner */}
      {!isAgent && <NotificationBanner pending={pending} />}

      {/* Termination Banner */}
      {isTerminated && (
        <div data-testid="termination-banner" className="border-2 border-[#DC2626] bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-red-100 flex items-center justify-center flex-shrink-0">
              <XCircle size={24} className="text-[#DC2626]" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading text-base font-bold text-[#DC2626]">Journey Terminated</h3>
              <p className="font-body text-sm text-[#0A0A0A] mt-1">
                <strong>{terminationInfo?.name || 'Agent'}</strong>&apos;s journey was ended at <strong>Day {terminationInfo?.termination_day}</strong>
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white border border-[#E5E7EB] p-3">
                  <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Rejected By</p>
                  <p className="font-body text-sm font-medium text-[#0A0A0A] mt-1">{terminationInfo?.terminated_by_name}</p>
                  <p className="font-body text-xs text-[#4B5563]">{terminationInfo?.terminated_by_role}</p>
                </div>
                <div className="bg-white border border-[#E5E7EB] p-3">
                  <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Date</p>
                  <p className="font-body text-sm font-medium text-[#0A0A0A] mt-1">{terminationInfo?.terminated_at ? new Date(terminationInfo.terminated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</p>
                </div>
                <div className="bg-white border border-[#E5E7EB] p-3 sm:col-span-1">
                  <p className="font-body text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5563]">Reason</p>
                  <p className="font-body text-sm text-[#0A0A0A] mt-1">{terminationInfo?.termination_note || 'No reason provided'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Selector Dropdown */}
      {!isAgent && (
        <div className="max-w-sm">
          <label className="font-body text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563] block mb-2">
            Select Trainee
          </label>
          <Select value={selectedAgent} onValueChange={setSelectedAgent}>
            <SelectTrigger data-testid="agent-selector" className="rounded-none border-[#E5E7EB]">
              <SelectValue placeholder="Choose a trainee..." />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {agents.map(a => (
                <SelectItem key={a.id} value={a.id} className="font-body text-sm">
                  {a.name} (@{a.username})
                </SelectItem>
              ))}
              {agents.length === 0 && (
                <div className="px-4 py-2 text-sm text-[#4B5563]">No agents found</div>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Phase legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(PHASE_STYLES).map(([phase, cls]) => (
          <span key={phase} className={`inline-block px-3 py-1 text-xs font-body font-semibold border ${cls}`}>{phase}</span>
        ))}
      </div>

      {/* Timeline */}
      {selectedAgent && progress.length > 0 ? (
        <div className="space-y-3">
          {structure.map((day) => (
            <StepCard
              key={day.day}
              day={day}
              structure={structure}
              progress={getProgressForDay(day.day)}
              canTrainerAct={canTrainerAct && !isTerminated}
              canManagerAct={canManagerAct && !isTerminated}
              canVpAct={canVpAct && !isTerminated}
              isAgent={isAgent}
              onTrainerAction={handleTrainerAction}
              onManagerAction={handleManagerAction}
              onVpAction={handleVpAction}
            />
          ))}
        </div>
      ) : selectedAgent ? (
        <p className="font-body text-sm text-[#4B5563] text-center py-12">No progress data found for this trainee.</p>
      ) : (
        <p className="font-body text-sm text-[#4B5563] text-center py-12">Select a trainee to view their journey.</p>
      )}
    </div>
  );
}
